import type { SetupContext } from 'vue'
import type {
  IToolingListComposable,
  IToolingListComposableProps,
  SetEnablePayload,
} from '../../../composables'
import type { Pagination } from '../../../models'
import type { Equalize } from '../../../modules/equalize'

// @ts-strict-ignore
import { ConflictApiError, DomainObjectUtils } from '@empathyco/playboard-core-domain'
import { getCurrentInstance } from 'vue'

import { mapActions, useApiError } from '../../../composables'

import { EqualizeFeatureEnum, FeatureIdEnum } from '../../../enums'

import { ROUTER_CONSTS } from '../../../router/router.consts'

import { TOOLING_CONSTS } from '../../../store/tooling/tooling.consts'

import { buildMessagesWithSeparator } from '../../../utils/messages.utils'

import { computedFn as equalizeComputedFn } from './computed'

interface IEqualizeViewDataMethodsProps {
  toolingList: IToolingListComposable<Equalize>
}

interface IEqualizeMethods {
  getFeatureClasses: (
    baseClass: string,
    dataItem: Equalize,
    feature: EqualizeFeatureEnum,
  ) => Array<string | Record<string, boolean>>
  getFeatureIcon: (feature: EqualizeFeatureEnum) => string
  onPaginationChange: (paginationValue: Pagination) => void
  onCheckboxClick: (dataItem: Equalize) => void
  onRowClick: (dataItem: Equalize) => void
  setEnabled: (payload: SetEnablePayload<any>) => Promise<void>
}

export const methodsFn = (
  props: IToolingListComposableProps,
  ctx: SetupContext,
  { toolingList }: IEqualizeViewDataMethodsProps,
): IEqualizeMethods => {
  const { emit } = ctx

  const {
    proxy: { $store, $epb },
  } = getCurrentInstance()

  const { excludedRowsIds } = equalizeComputedFn(props, ctx, { toolingList })

  const { pagination } = toolingList

  const { getErrorMessage } = useApiError()

  const { updateAction } = mapActions($store, TOOLING_CONSTS.moduleName, {
    updateAction: TOOLING_CONSTS.actions.update,
  })

  const hasFeatureValue = (feature: EqualizeFeatureEnum, dataItem: Equalize): boolean => {
    let hasFeature = false
    switch (feature) {
      case EqualizeFeatureEnum.BOOSTING:
        hasFeature = !!dataItem.features?.boosting.config.boosts.length
        break
      case EqualizeFeatureEnum.MULTI_MATCH_SEARCH:
        hasFeature = !!dataItem.features?.multiMatchSearch.config.searchableFields.length
        break
      case EqualizeFeatureEnum.FUNCTION_SCORE:
        hasFeature = !!dataItem.features?.functionScore.config.functions.length
        break
    }
    return hasFeature
  }

  const getFeatureClasses = (
    baseClass: string,
    dataItem: Equalize,
    feature: EqualizeFeatureEnum,
  ): (string | Record<string, boolean>)[] => {
    return [
      `${baseClass}__feature eicon`,
      {
        [`${baseClass}__feature--disabled`]: !hasFeatureValue(feature, dataItem),
      },
    ]
  }

  const getFeatureIcon = (feature: EqualizeFeatureEnum): string => {
    const iconOptions = {
      [EqualizeFeatureEnum.MULTI_MATCH_SEARCH]: 'textualmatch',
      [EqualizeFeatureEnum.FUNCTION_SCORE]: 'functionscore',
      [EqualizeFeatureEnum.BOOSTING]: 'business',
    }
    return iconOptions[feature]
  }

  const onPaginationChange = (paginationValue: Pagination): void => {
    pagination.value = paginationValue
    toolingList.onPaginationChange(paginationValue)
  }

  const onCheckboxClick = (dataItem: Equalize): void => {
    if (excludedRowsIds.value.includes(dataItem.id)) {
      toolingList.navigateToRoute(ROUTER_CONSTS.routes[FeatureIdEnum.EQUALIZE].update, dataItem)
    }
  }

  const onRowClick = (dataItem: Equalize): void => {
    toolingList.navigateToRoute(ROUTER_CONSTS.routes[FeatureIdEnum.EQUALIZE].update, dataItem)
  }

  const setEnabled = async ({ enabled }: SetEnablePayload<any>): Promise<void> => {
    emit('updateLoading', { visible: true })
    const items = toolingList.optionSelected.value
      ? [toolingList.optionSelected.value]
      : toolingList.selection.value
    const updateItems = items.map(({ id: selectedId }) => {
      const updateItem = DomainObjectUtils.clone(
        toolingList.data.value.results.find(dataItem => dataItem.id === selectedId),
      )
      updateItem.criteria.enabled = enabled
      return updateItem
    })
    try {
      await updateAction({
        tool: FeatureIdEnum.EQUALIZE,
        items: updateItems,
      })
      const messages = $epb.messages.tools.equalize
      emit('showSnackbar', enabled ? messages.enable.success : messages.disable.success)
    } catch (errors) {
      emit('updateLoading', { visible: false })
      handleUpdateErrors(errors)
    } finally {
      await toolingList.loadData()
    }
  }

  function handleUpdateErrors(errors): void {
    const messages = $epb.messages.tools.equalize
    const conflictingElementIds = getConflictingElementIds(errors)
    if (conflictingElementIds) {
      const message = $epb.utils.compileMessage(messages.conflictErrorList.message, {
        ids: buildMessagesWithSeparator(conflictingElementIds, ', ', $epb.messages.app.and, true),
      })
      emit('showCustomError', {
        title: messages.conflictErrorList.title,
        message,
      })
    } else {
      emit('showError', getErrorMessage(errors[0]))
    }
  }

  function getConflictingElementIds(errors): string[] {
    return errors.reduce((conflictingElementIds: string[], error) => {
      if (error instanceof ConflictApiError) {
        conflictingElementIds.push(error.conflictingElements[0].id)
      }
      return conflictingElementIds
    }, []) as string[]
  }

  return {
    getFeatureClasses,
    getFeatureIcon,
    onPaginationChange,
    onCheckboxClick,
    onRowClick,
    setEnabled,
  }
}
